import { useTheme } from '@components/theme';
import UserContext from '@context/User/user-context';
import { userIsDesignerOrAdmin } from '@lib/auth/auth';
import { isOpenInstallAppBannerDismissed } from '@lib/cookie/openInstallAppBannerDismissed';
import { getResizedImageUrl } from '@lib/image';
import browser from '@utils/browser/browser';
import classNames from 'classnames';
import NextLink from 'next/link';
import { useContext } from 'react';
import { useMinBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import { navigationHeights } from '../../../../../styles/variables';
import styles from './SaleHero.module.scss';
import Logo from './assets/cm_sale_logo.svg';
import SaleBanner from '../Hero/SaleBanner/SaleBanner';

interface ISaleHero {
  description: string | JSX.Element;
  ctaText: string;
  ctaLink: string;
  ctaOnClick: (e: any) => void;
  className?: string;
}

export default function SaleHero({
  description,
  ctaText,
  ctaLink,
  ctaOnClick,
  className = '',
}: ISaleHero) {
  const theme = useTheme();
  const { user } = useContext(UserContext);
  const isAdminOrDesigner = userIsDesignerOrAdmin(user);
  const isMobile = !useMinBreakpoint('small-desktop');

  const shouldPadHeroText = browser.isIOS() && !isOpenInstallAppBannerDismissed();

  return (
    <section
      className={classNames(styles.Hero, className)}
      style={{
        paddingTop: isAdminOrDesigner ? 0 : `${navigationHeights.mainNavHeight}px`
      }}
    >
      {!isMobile && <SaleBanner />}
      <div className={styles.ImageContainer}>
        <div className={styles.AspectRatioBox}>
          <div className={styles.AspectRatioBoxInside}>

            <picture>
              <source
                media="(min-width: 768px)"
                srcSet={getResizedImageUrl({
                  quality: 90,
                  url: 'https://static.havenly.com/home/hero/2025/cm_hero_2025.png',
                  width: 1250,
                  height: 810,
                  valign: 'top'
                })
                }
              />
              <img
                src={getResizedImageUrl({
                  quality: 90,
                  url: 'https://static.havenly.com/home/hero/2025/cm_hero_2025.png',
                  width: 750,
                  height: 1100,
                  halign: 'right'
                })}
                alt="Hero"
                className={styles.HeroImage}
              />
            </picture>

            <div className={styles.Backdrop} />
          </div>

        </div>

        <div className={`${styles.ContentContainer} ${shouldPadHeroText ? styles.Padded : ''}`}>
          <h1 className={classNames(styles.Title)}>
            <Logo />
          </h1>

          <p className={styles.Subtitle}>THE #1 INTERIOR DESIGN SERVICE</p>
          <p className={styles.Description}>
            {description}
          </p>

          <NextLink
            href={ctaLink}
            prefetch
          >
            <button
              type="button"
              className={classNames(theme.styles.Button,
                theme.styles.Primary,
                styles.CTAButton)}
              data-test={'homepage-hero-cta-link'}
              onClick={ctaOnClick}
            >
              {ctaText}
            </button>
          </NextLink>
        </div>
      </div>
      {isMobile && <SaleBanner />}
    </section>
  );
}
